import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from '../../../lib/i18n';
import { useWorkspaces } from '../../../lib/workspaces';
import { useMyAgents } from '../../../lib/my-agents';
import { useLoadBi } from '../../../lib/use-load-bi';
import { ENTITY_TYPE, useGenericBi } from '../../../lib/use-generic-bi';
import { useOpenSideChat } from '../../../lib/use-open-side-chat';
import { useNarrow } from '../../../lib/use-narrow';
import { useSessionSpaceFilter } from '../../../lib/use-session-space-filter';
import { useRoutines } from '../../../lib/routines';
import {
  agentsOf,
  clampRoutineFilters,
  filterRoutines,
  hasActiveRoutineFilters,
  spacesOf,
  type RoutineFilterState,
} from '../../../lib/routine-filters';
import { ROUTINES_PAGE } from '../../../lib/routines-bi';
import {
  parseRoutineTab,
  routinesInTab,
  ROUTINE_TAB_EMPTY_KEY,
  CATEGORY_ROUTINE_TAB_LABEL_KEY,
  ROUTINE_TABS,
  type RoutineTab,
} from '../../../lib/routine-tabs';
import { routineConnectorSections } from '../../../lib/routine-groups';
import { queryKeys } from '../../../lib/query-client';
import { fetchConnectors } from '../../../lib/connectors';
import { useAgentName } from '../../../lib/use-agent-name';
import { Button, Card, Tabs } from '../ui';
import { RoutineCard } from './RoutineCard';
import { RoutineConnectorCard, RoutineGroupHeaderRow } from './RoutineConnectorCard';
import { CategoryHeader, CategoryList, CategoryPage, CategoryToolbar } from '../category-view/CategoryView';
import { RoutineDetailModal } from './RoutineDetailModal';
import { RoutinesFilterBar } from './RoutinesFilterBar';
import styles from './routines.module.css';
import { TabConnectHint } from '../category-view/TabConnectHint';
import { RoutinesEmptyState } from '../category-view/CategoryEmptyState';
import { isHomeEmptyForced } from '../../../lib/dev-home-empty-states';

// THE one Symphony routines renderer — self-contained (fetches its own data), scoped by two axes so one
// component serves every context, exactly as `TasksBody` does for Ongoing Tasks:
//   - GLOBAL (no filter)          — the `/routines` page.
//   - PER-WORKSPACE (workspaceId) — a Space's Dashboard tab.
//   - PER-AGENT (agentId)         — arriving from an agent.
//
// `filterable` adds the two-axis filter row and is for the STANDALONE page only: an embedded host has
// already fixed its scope, and a picker for an axis the host has decided is a control that can only take
// the screen somewhere the host doesn't want it. It also changes what `workspaceId` MEANS — with
// `filterable` it is the SELECTED Space (read off `/routines?workspace=<slug>` by the page), so the list
// is fetched account-wide and narrowed here, which is what lets a user widen back to All Spaces.
export function RoutinesBody({
  workspaceId,
  agentId,
  header,
  filterable: filterableProp,
  onHome,
  recommendedOnly,
  action,
}: {
  workspaceId?: string;
  agentId?: string;
  header?: ReactNode;
  filterable?: boolean;
  /** Hosted by the home's Routines category: account-wide and filterable, with the Space and the tab
   *  held here rather than in the URL, and no create button of its own — the home draws it. */
  onHome?: boolean;
  /** Embedded by the Wins page: the Recommended tab's list on its own, account-wide, with no tab strip,
   *  filters or create button — the host owns the page chrome. */
  recommendedOnly?: boolean;
  /** The home's create button, at the toolbar's end. */
  action?: ReactNode;
}) {
  const { t } = useTranslation();
  const router = useRouter();
  const { logPageClickBi } = useGenericBi();
  const openSideChat = useOpenSideChat();
  const narrow = useNarrow();
  const filterable = filterableProp || onHome || recommendedOnly;
  const [homeSpace, setHomeSpace] = useState('');
  // A standalone page holds its Space pick for the visit only (`useSessionSpaceFilter`).
  const [pageSpace, setPageSpace] = useSessionSpaceFilter(workspaceId, !!filterableProp && !onHome);
  // `null` until decided — see `homeOpeningTab` below.
  const [homeTab, setHomeTab] = useState<RoutineTab | null>(null);
  const { state } = useRoutines(filterable ? undefined : workspaceId);
  const connectorsQuery = useQuery({ queryKey: queryKeys.connectors, queryFn: fetchConnectors });
  useLoadBi('routines-list-data', { isLoading: state.kind === 'loading', isError: state.kind === 'error' });

  const workspacesState = useWorkspaces();
  const teamState = useMyAgents();
  const workspaceRoster = workspacesState.status === 'ready' ? workspacesState.workspaces : [];

  const all = state.kind === 'ready' ? state.routines : [];
  // A host's `workspaceId` is applied server-side; `agentId` narrows client-side.
  const agentScoped = all.filter((routine) => !agentId || routine.agentId === agentId);
  // The dev switch shows the home as an account with no routines of its own (Figma 21853:127010).
  const scoped = onHome && isHomeEmptyForced() ? routinesInTab(agentScoped, 'recommended') : agentScoped;

  // ---- The two tabs: Yours | Recommended for you (Figma 18352-340460 / 18352-340192) --------------
  // A connector kit's offers — installed paused, never switched on — sit on their own tab so the list
  // of what actually runs is not padded with suggestions. The tab lives in the URL like the Space does,
  // so a link opens on the tab it was copied from. Only the STANDALONE page has tabs: an embedded host
  // (a Space's dashboard) shows everything, as it always did.
  // On the home, an account with nothing of its own yet opens on Recommended (Figma 21853:127010). Decided
  // once, when the routines first arrive, so switching one on — which moves it to Yours — does not move the
  // reader to the other tab under them. The same latch Tasks holds on its opening tab.
  const homeOpeningTab: RoutineTab =
    routinesInTab(scoped, 'yours').length === 0 && routinesInTab(scoped, 'recommended').length > 0 ? 'recommended' : parseRoutineTab(undefined);
  useEffect(() => {
    if (onHome && homeTab === null && state.kind === 'ready') setHomeTab(homeOpeningTab);
  }, [onHome, homeTab, state.kind, homeOpeningTab]);
  const tab: RoutineTab = recommendedOnly ? 'recommended' : onHome ? (homeTab ?? homeOpeningTab) : parseRoutineTab(router.query.tab);
  const tabScoped = filterable ? routinesInTab(scoped, tab) : scoped;

  // Options are DERIVED FROM THE ROUTINES IN SCOPE — the routines on THIS tab — never from the full
  // roster, so a menu can never offer a value that matches nothing. An axis the host has fixed offers
  // nothing at all, which is how the bar knows not to draw a picker for it.
  //
  // …unless that leaves a menu with nothing in it. A Maestro-run routine carries no Space and no agent, so an
  // account whose routines are all the Maestro's would get no filter row at all, where every category draws
  // "All spaces ⌄ · All agents ⌄". Then the menu offers the whole roster, and a pick that matches nothing
  // says so with the "no match" line and its Clear.
  const teamRoster = teamState.status === 'ready' ? teamState.team.agents.map((agent) => ({ id: agent.id, name: agent.name?.trim() ?? '' })) : [];
  const spacesInScope = filterable ? spacesOf(tabScoped, workspaceRoster) : [];
  const spaceOptions = filterable && spacesInScope.length === 0 ? workspaceRoster : spacesInScope;
  const agentsInScope = filterable && !agentId ? agentsOf(tabScoped, teamRoster) : [];
  const agentOptions = filterable && !agentId && agentsInScope.length === 0 ? teamRoster.filter((agent) => agent.name) : agentsInScope;
  // The agent pick is this component's own, as on Tasks; only the Space rides the URL.
  const [agentPick, setAgentPick] = useState('');

  // The Space lives in the URL — the view stays shareable and the back button works. It is clamped once
  // the data AND the roster have landed: clamping against nothing would silently widen a shared
  // `?workspace=` link to every Space for a frame.
  const requested: RoutineFilterState = { workspaceId: onHome ? homeSpace : filterable ? pageSpace : '', agentId: agentPick };
  const resolved =
    state.kind === 'ready' && workspacesState.status === 'ready' && teamState.status === 'ready';
  const filters = resolved ? clampRoutineFilters(requested, spaceOptions, agentOptions) : requested;
  const visible = filterRoutines(tabScoped, filters);

  // The screen is grouped by CONNECTOR, one card each, in the connectors screen's own order so the two
  // agree. Filtering happens first and the grouping is applied to what survived: a connector whose
  // routines were all filtered out has no card, rather than an empty one saying the filter found nothing
  // here specifically.
  //
  // The list waits for the connectors read as well as the routines one. The connectors decide the cards'
  // ORDER and their titles, the two queries land in whichever order the network gives them, and
  // routines-first is the common case — which would lay the cards out and then reorder and rename them a
  // moment later. Waiting for BOTH costs one skeleton frame.
  //
  // An ERROR does not wait, though — a connectors read that will never succeed must not hold the
  // routines screen empty forever. The cards are then ordered and titled from the slugs alone, which is
  // the honest answer when we cannot ask the connectors screen what an app is called.
  const connectorsSettled = connectorsQuery.status !== 'loading';
  const connectorOrder = (connectorsQuery.data?.connectors ?? [])
    .filter((connector) => connector.connected === true)
    .map((connector) => (connector.toolkit ?? '').toLowerCase())
    .filter(Boolean);
  const sections = routineConnectorSections(visible, connectorOrder);
  const sectionKey = (section: (typeof sections)[number]) => section.slug ?? 'own';

  const spaceName = (id?: string): string | undefined => {
    // With the Space axis fixed by the host or by the picker, naming it on every card says nothing.
    if (!filterable || filters.workspaceId) return undefined;
    const wid = id || workspaceId;
    return wid ? workspaceRoster.find((w) => w.id === wid)?.name : undefined;
  };

  const filterBi = (name: string) =>
    logPageClickBi({ ...ROUTINES_PAGE, button_name: name }, { componentName: name.replace(/_/g, '-') });

  const applyFilters = (next: RoutineFilterState) => {
    setAgentPick(next.agentId);
    if (next.workspaceId === filters.workspaceId) return;
    if (onHome) {
      setHomeSpace(next.workspaceId);
      return;
    }
    setPageSpace(next.workspaceId);
  };

  // The default tab is spelled by its ABSENCE, so the plain `/routines` link stays the canonical one.
  const selectTab = (next: RoutineTab) => {
    if (next === tab) return;
    logPageClickBi(
      { ...ROUTINES_PAGE, button_name: 'switch_tab' },
      { componentName: `switch_tab_${next}`, entity: { id: next, type: ENTITY_TYPE.tab } },
    );
    if (onHome) {
      setHomeTab(next);
      return;
    }
    const query = { ...router.query };
    if (next === 'recommended') query.tab = next;
    else delete query.tab;
    void router.push({ pathname: router.pathname, query }, undefined, { shallow: true });
  };

  // One drill-in at a time, held as an ID and resolved from the list on every render — never as a
  // snapshot of the entity. A cached object would go stale the moment the modal's own switch or edit
  // patched the cache, which is exactly how the WhatsApp group modal used to spring back.
  const [openId, setOpenId] = useState<string | null>(null);
  const [openInEdit, setOpenInEdit] = useState(false);
  const open = visible.find((routine) => routine.id === openId) ?? null;
  const openDetail = (id: string, edit: boolean) => {
    setOpenInEdit(edit);
    setOpenId(id);
  };

  const empty = state.kind === 'ready' && scoped.length === 0;
  const homeEmpty = !!onHome && empty;

  // "Add a new routine" — the same handoff Goals' create CTA uses, and for the same reason: a routine is
  // authored by the assistant through its own tools, so the button opens a fresh chat already carrying
  // the ask rather than this screen owning a form. Both phones' sticky bar seeds the identical message.
  const createRoutine = () => {
    logPageClickBi({ ...ROUTINES_PAGE, button_name: 'create_routine' });
    openSideChat({ kind: 'new', message: t('routines.create.prompt') });
  };
  const createButton = (
    <Button onClick={createRoutine}>
      {t('categoryView.routines.createCta')}
    </Button>
  );

  return (
    <div className={styles.body}>
      {header}
      <CategoryPage>
        {/* The home names the category itself; a standalone page's PageHeader already does. */}
        <CategoryHeader
          title={onHome ? t('categoryView.routines.title') : undefined}
          description={onHome && !homeEmpty ? <TabConnectHint tab="routines" fallback={t('categoryView.routines.description')} /> : undefined}
          tight={homeEmpty}
          filters={
            filterable && !recommendedOnly && state.kind === 'ready' && !empty ? (
              <RoutinesFilterBar spaces={spaceOptions} agents={agentOptions} filters={filters} onChange={applyFilters} onBi={filterBi} />
            ) : undefined
          }
        />
        {/* Nothing to switch on an account with no routines — but the CREATE action stays, because an empty
            screen with no way to make the first routine is a dead end. On a phone the standalone screen's
            create is the sticky bar at the foot instead (see `.createBar` below), where both apps put it. */}
        {filterable && !recommendedOnly && state.kind === 'ready' && !homeEmpty && (
          <CategoryToolbar action={onHome ? action : narrow ? undefined : createButton}>
            {!empty && (
              <Tabs
                variant="category"
                items={ROUTINE_TABS.map((key) => ({ value: key, label: t(CATEGORY_ROUTINE_TAB_LABEL_KEY[key]) }))}
                value={tab}
                onChange={selectTab}
                label={t('routines.title')}
              />
            )}
          </CategoryToolbar>
        )}

        {(state.kind === 'loading' || (state.kind === 'ready' && !empty && !connectorsSettled)) && <LoadingSkeleton />}
        {state.kind === 'error' && <div className={styles.error}>{t('routines.error.title')}</div>}
        {/* An account with NO routines does not wait on the connectors read — there is nothing to group. */}
        {state.kind === 'ready' &&
          (empty || connectorsSettled) &&
          (homeEmpty ? (
            <RoutinesEmptyState onCreate={createRoutine} />
          ) : empty ? (
            <EmptyState />
          ) : visible.length === 0 ? (
            <Card className={styles.empty}>
              {/* An account with routines that has been narrowed to none has not run out of routines —
                  saying "No routines yet" there is untrue and hides the filter that emptied the screen. An
                  empty TAB says which absence it is: nothing adopted yet, or nothing on offer. */}
              <div className={styles.emptyHint}>
                {t(hasActiveRoutineFilters(filters) ? 'routines.filter.noMatch' : ROUTINE_TAB_EMPTY_KEY[tab])}
              </div>
            </Card>
          ) : (
            (() => {
              // The CARD is identical in both layouts — only where it sits changes.
              const cardsOf = (section: (typeof sections)[number]) =>
                section.routines.map((routine) => (
                  <RoutineCard
                    key={routine.id}
                    routine={routine}
                    flat
                    spaceName={spaceName(routine.workspaceId)}
                    rosterName={routine.agentId ? teamRoster.find((agent) => agent.id === routine.agentId)?.name : undefined}
                    onOpen={() => openDetail(routine.id, false)}
                  />
                ));

              // A phone keeps one card per connector, its rows inside it (Figma 21055:250806).
              if (narrow) {
                return (
                  <div className={styles.list}>
                    {sections.map((section) => (
                      <RoutineConnectorCard key={sectionKey(section)} slug={section.slug} count={section.routines.length}>
                        {cardsOf(section)}
                      </RoutineConnectorCard>
                    ))}
                  </div>
                );
              }

              // A desktop draws ONE list card: each connector a header row over its routines (Figma
              // 21055:248873).
              return (
                <CategoryList>
                  {sections.map((section) => (
                    <Fragment key={sectionKey(section)}>
                      <RoutineGroupHeaderRow slug={section.slug} count={section.routines.length} />
                      {cardsOf(section)}
                    </Fragment>
                  ))}
                </CategoryList>
              );
            })()
          ))}
      </CategoryPage>

      {/* The phones pin this above the scroll; mobile web does the same rather than losing it off the
          end of a long list. Desktop keeps it in the filter row. */}
      {filterable && !recommendedOnly && state.kind === 'ready' && narrow && !onHome && (
        <div className={styles.createBar}>{createButton}</div>
      )}

      {open && (
        <RoutineDetailModal
          key={open.id}
          routine={open}
          startInEdit={openInEdit}
          spaceName={workspaceRoster.find((w) => w.id === open.workspaceId)?.name}
          onClose={() => setOpenId(null)}
        />
      )}
    </div>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  const agent = useAgentName();
  return (
    <Card className={styles.empty}>
      <div className={styles.emptyTitle}>{t('routines.empty.title')}</div>
      <div className={styles.emptyHint}>{t('routines.empty.hint', { maestroAgentName: agent.short })}</div>
    </Card>
  );
}

function LoadingSkeleton() {
  return (
    <div className={styles.list}>
      {[70, 55, 62].map((w, i) => (
        <Card key={i} size="sm" className={styles.skelCard}>
          <div className="orion-shimmer" style={{ height: 14, width: `${w}%` }} />
          <div className="orion-shimmer" style={{ height: 10, width: `${w - 20}%`, marginTop: 10 }} />
          <div className="orion-shimmer" style={{ height: 10, width: `${w - 30}%`, marginTop: 16 }} />
        </Card>
      ))}
    </div>
  );
}
